/**
 * BaseMedicalFacility.js
 * PSP/CRE Tool v2.0 - Medical Facility Models
 * 
 * Base class for all medical treatment facilities (Role 1, 2, 3, 4)
 * Provides common functionality for capacity management, supply tracking,
 * patient flow, and EVACUATION ASSET MANAGEMENT.
 * 
 * Author: Dr. Tatar Radu MD
 * Institution: Romanian Military Medical Institute
 * Date: December 26, 2024
 * Version: 2.0 - Phase 2 Evacuation Asset Architecture (EA-001, EA-002, EA-003)
 * 
 * PHASE 2 CHANGES (December 26, 2024):
 * - EA-001 FIX: Added per-facility evacuation_assets object
 * - EA-002 FIX: Added organic/attached/detached/effective asset tracking
 * - EA-003 FIX: Added evacuation_config with PULL/PUSH/AUTO methods
 * - Added asset allocation methods
 * - Added evacuation chain configuration
 * - Added asset effectiveness calculations
 */

// ============================================================================
// EVACUATION ASSET DEFAULTS
// ============================================================================

/**
 * Default evacuation asset structure for a medical facility
 * These values are overridden by Role-specific defaults
 */
export const DEFAULT_EVACUATION_ASSETS = {
  ground_ambulances: {
    wheeled_light: 0,
    wheeled_armored: 0,
    tracked: 0
  },
  helicopters: {
    allocated: 0,      // Helicopters are ALLOCATED, not organic (except Aviation units)
    on_call: 0
  },
  personnel: {
    medics: 0,
    drivers: 0,
    litter_bearers: 0,
    flight_medics: 0
  }
};

/**
 * Evacuation methods per NATO doctrine
 */
export const EVACUATION_METHODS = {
  PULL: {
    code: 'PULL',
    label: 'Pull System',
    description: 'Receiving facility sends assets to pick up patients',
    icon: '⬅️',
    typical_use: 'Role 2 → Role 1 evacuation',
    advantages: [
      'Receiving facility controls timing',
      'Can batch multiple pickups',
      'Lower asset utilization at sending facility'
    ],
    disadvantages: [
      'Longer wait times at sending facility',
      'Requires communication for pickup request'
    ]
  },
  PUSH: {
    code: 'PUSH',
    label: 'Push System',
    description: 'Sending facility pushes patients forward when stabilized',
    icon: '➡️',
    typical_use: 'Point of Injury → Role 1 evacuation',
    advantages: [
      'Faster initial evacuation',
      'Sending facility controls patient flow',
      'Better for high-volume scenarios'
    ],
    disadvantages: [
      'Can overwhelm receiving facility',
      'Requires receiving facility readiness'
    ]
  },
  AUTO: {
    code: 'AUTO',
    label: 'Automatic/Hybrid',
    description: 'System automatically selects based on capacity and urgency',
    icon: '🔄',
    typical_use: 'Default for most operations',
    logic: [
      'T1 IMMEDIATE: Always PUSH',
      'T2 DELAYED: PULL if receiving has capacity',
      'T3 MINIMAL: Batched PULL'
    ]
  }
};

// ============================================================================
// BASE MEDICAL FACILITY CLASS
// ============================================================================

export class BaseMedicalFacility {
  /**
   * Create a medical facility
   * 
   * @param {Object} config - Facility configuration
   * @param {string} config.facility_id - Unique identifier
   * @param {string} config.facility_name - Human-readable name
   * @param {number} config.role_level - Role level (1, 2, 3, or 4)
   * @param {string} config.facility_type - Type (ROLE1, ROLE2_BASIC, etc.)
   * @param {string} [config.parent_unit_id] - ID of parent military unit
   * @param {string} [config.parent_unit_type] - Type of parent
   * @param {number} [config.position_feba_km=0] - Distance from FEBA in km
   * @param {number} [config.beds=10] - Number of beds
   * @param {number} [config.surgical_teams=0] - Number of surgical teams
   * @param {number} [config.icu_beds=0] - Number of ICU beds
   */
  constructor(config) {
    // ========================================================================
    // IDENTIFICATION
    // ========================================================================
    this.facility_id = config.facility_id;
    this.facility_name = config.facility_name;
    this.role_level = config.role_level;           // 1, 2, 3, or 4
    this.facility_type = config.facility_type;     // ROLE1, ROLE2_BASIC, etc.
    
    // Parent unit linkage
    this.parent_unit_id = config.parent_unit_id || null;
    this.parent_unit_type = config.parent_unit_type || null;
    
    // Hierarchy chain (for display and tracing)
    this.hierarchy = config.hierarchy || {
      corps: null,
      division: null,
      brigade: null,
      battalion: null
    };
    
    // ========================================================================
    // POSITIONING
    // ========================================================================
    this.position_feba_km = config.position_feba_km || 0;
    
    // ========================================================================
    // CAPACITY
    // ========================================================================
    this.beds = config.beds || 10;
    this.surgical_teams = config.surgical_teams || 0;
    this.icu_beds = config.icu_beds || 0;
    
    // ========================================================================
    // PERSONNEL (initialize empty, set from Tab 3)
    // ========================================================================
    this.personnel = config.personnel || {
      medical_officers: 0,
      nurses: 0,
      medics: 0,
      support_staff: 0
    };
    
    // ========================================================================
    // SUPPLIES (initialize empty, set from Tab 4)
    // ========================================================================
    this.supplies = config.supplies || {
      trauma_kits: 0,
      bandages: 0,
      iv_fluids: 0,
      blood_units: 0,
      antibiotics: 0,
      surgical_supplies: 0,
      tourniquets: 0,
      txa_doses: 0
    };
    
    // ========================================================================
    // PHASE 2: EVACUATION ASSETS (EA-001, EA-002)
    // ========================================================================
    
    /**
     * Organic assets: Assets that belong to this facility's parent unit
     * These are the baseline assets the facility has from its TO&E
     */
    this.organic_assets = config.organic_assets || this._getDefaultOrganicAssets();
    
    /**
     * Attached assets: Assets temporarily assigned from another unit
     * Track source for potential recall
     */
    this.attached_assets = config.attached_assets || {
      ground_ambulances: { wheeled_light: 0, wheeled_armored: 0, tracked: 0 },
      helicopters: { allocated: 0, on_call: 0 },
      personnel: { medics: 0, drivers: 0, litter_bearers: 0, flight_medics: 0 },
      sources: []  // Array of { source_facility_id, asset_type, count, attached_date }
    };
    
    /**
     * Detached assets: Organic assets temporarily sent to support another facility
     * Track destination for potential recall
     */
    this.detached_assets = config.detached_assets || {
      ground_ambulances: { wheeled_light: 0, wheeled_armored: 0, tracked: 0 },
      helicopters: { allocated: 0, on_call: 0 },
      personnel: { medics: 0, drivers: 0, litter_bearers: 0, flight_medics: 0 },
      destinations: []  // Array of { dest_facility_id, asset_type, count, detached_date }
    };
    
    // ========================================================================
    // PHASE 2: EVACUATION CONFIGURATION (EA-003)
    // ========================================================================
    
    /**
     * Evacuation chain configuration
     * Defines how patients flow through the medical system
     */
    this.evacuation_config = config.evacuation_config || {
      // Evacuation method for incoming patients
      incoming_method: 'AUTO',           // PULL, PUSH, or AUTO
      
      // Evacuation method for outgoing patients  
      outgoing_method: 'AUTO',           // PULL, PUSH, or AUTO
      
      // Facilities this facility picks up from (PULL)
      picks_up_from: [],                 // Array of facility_ids
      
      // Facility this sends patients to
      delivers_to: null,                 // Single facility_id
      
      // Priority for helicopter allocation
      helicopter_priority: 'NORMAL',     // LOW, NORMAL, HIGH, CRITICAL
      
      // Maximum hold time before forced evacuation (hours)
      max_hold_time_hours: config.max_hold_time_hours || 4,
      
      // Evacuation triggers
      triggers: {
        capacity_threshold: 80,          // % capacity that triggers evacuation
        time_threshold_hours: 4,         // Hours before forced evac
        t1_immediate_evac: true          // Always evac T1 immediately
      }
    };
    
    // ========================================================================
    // OPERATIONAL STATUS
    // ========================================================================
    this.operational = true;
    this.beds_occupied = 0;
    this.patients_current = [];
    
    // ========================================================================
    // STATISTICS TRACKING
    // ========================================================================
    this.stats = {
      total_patients_treated: 0,
      total_surgeries_performed: 0,
      total_evacuations_sent: 0,
      total_evacuations_received: 0,
      total_returned_to_duty: 0,
      total_died_of_wounds: 0,
      // Phase 2: Asset utilization stats
      ground_evac_missions: 0,
      air_evac_missions: 0,
      average_evac_time_minutes: 0
    };
    
    // ========================================================================
    // METADATA
    // ========================================================================
    this.created_at = new Date();
    this.version = '2.0';
  }
  
  // ============================================================================
  // PHASE 2: DEFAULT ORGANIC ASSETS BY ROLE
  // Override in subclasses for role-specific defaults
  // ============================================================================
  
  /**
   * Get default organic assets for this facility type
   * Override in subclasses (Role1Facility, Role2BasicFacility, etc.)
   * 
   * @returns {Object} Default organic assets
   */
  _getDefaultOrganicAssets() {
    return {
      ground_ambulances: {
        wheeled_light: 0,
        wheeled_armored: 0,
        tracked: 0
      },
      helicopters: {
        allocated: 0,
        on_call: 0
      },
      personnel: {
        medics: 0,
        drivers: 0,
        litter_bearers: 0,
        flight_medics: 0
      }
    };
  }
  
  // ============================================================================
  // PHASE 2: EFFECTIVE ASSETS CALCULATION (EA-002)
  // ============================================================================
  
  /**
   * Calculate effective assets available to this facility
   * Effective = Organic + Attached - Detached
   * 
   * @returns {Object} Effective assets currently available
   */
  getEffectiveAssets() {
    const effective = {
      ground_ambulances: {
        wheeled_light: 0,
        wheeled_armored: 0,
        tracked: 0,
        total: 0
      },
      helicopters: {
        allocated: 0,
        on_call: 0,
        total: 0
      },
      personnel: {
        medics: 0,
        drivers: 0,
        litter_bearers: 0,
        flight_medics: 0,
        total: 0
      }
    };
    
    // Calculate for each asset category
    ['wheeled_light', 'wheeled_armored', 'tracked'].forEach(type => {
      effective.ground_ambulances[type] = 
        (this.organic_assets.ground_ambulances?.[type] || 0) +
        (this.attached_assets.ground_ambulances?.[type] || 0) -
        (this.detached_assets.ground_ambulances?.[type] || 0);
    });
    
    ['allocated', 'on_call'].forEach(type => {
      effective.helicopters[type] = 
        (this.organic_assets.helicopters?.[type] || 0) +
        (this.attached_assets.helicopters?.[type] || 0) -
        (this.detached_assets.helicopters?.[type] || 0);
    });
    
    ['medics', 'drivers', 'litter_bearers', 'flight_medics'].forEach(type => {
      effective.personnel[type] = 
        (this.organic_assets.personnel?.[type] || 0) +
        (this.attached_assets.personnel?.[type] || 0) -
        (this.detached_assets.personnel?.[type] || 0);
    });
    
    // Calculate totals
    effective.ground_ambulances.total = 
      effective.ground_ambulances.wheeled_light +
      effective.ground_ambulances.wheeled_armored +
      effective.ground_ambulances.tracked;
    
    effective.helicopters.total = 
      effective.helicopters.allocated +
      effective.helicopters.on_call;
    
    effective.personnel.total = 
      effective.personnel.medics +
      effective.personnel.drivers +
      effective.personnel.litter_bearers +
      effective.personnel.flight_medics;
    
    return effective;
  }
  
  /**
   * Get total ground ambulances available
   * 
   * @returns {number} Total ground ambulances
   */
  getTotalGroundAmbulances() {
    const effective = this.getEffectiveAssets();
    return effective.ground_ambulances.total;
  }
  
  /**
   * Get total helicopters available
   * 
   * @returns {number} Total helicopters
   */
  getTotalHelicopters() {
    const effective = this.getEffectiveAssets();
    return effective.helicopters.total;
  }
  
  // ============================================================================
  // PHASE 2: ASSET ATTACHMENT/DETACHMENT (EA-002)
  // ============================================================================
  
  /**
   * Attach assets from another facility
   * 
   * @param {string} sourceFacilityId - Source facility ID
   * @param {string} assetCategory - 'ground_ambulances', 'helicopters', or 'personnel'
   * @param {string} assetType - Specific type within category
   * @param {number} count - Number of assets to attach
   * @returns {Object} Result of attachment operation
   */
  attachAssets(sourceFacilityId, assetCategory, assetType, count) {
    if (!this.attached_assets[assetCategory]) {
      return { success: false, error: 'INVALID_CATEGORY' };
    }
    
    if (this.attached_assets[assetCategory][assetType] === undefined) {
      return { success: false, error: 'INVALID_ASSET_TYPE' };
    }
    
    // Attach the assets
    this.attached_assets[assetCategory][assetType] += count;
    
    // Track source for potential recall
    this.attached_assets.sources.push({
      source_facility_id: sourceFacilityId,
      asset_category: assetCategory,
      asset_type: assetType,
      count: count,
      attached_date: new Date().toISOString()
    });
    
    return {
      success: true,
      attached: count,
      new_total: this.attached_assets[assetCategory][assetType],
      effective: this.getEffectiveAssets()
    };
  }
  
  /**
   * Detach organic assets to support another facility
   * 
   * @param {string} destFacilityId - Destination facility ID
   * @param {string} assetCategory - Asset category
   * @param {string} assetType - Specific type
   * @param {number} count - Number to detach
   * @returns {Object} Result
   */
  detachAssets(destFacilityId, assetCategory, assetType, count) {
    const organic = this.organic_assets[assetCategory]?.[assetType] || 0;
    const alreadyDetached = this.detached_assets[assetCategory]?.[assetType] || 0;
    const available = organic - alreadyDetached;
    
    if (count > available) {
      return {
        success: false,
        error: 'INSUFFICIENT_ASSETS',
        requested: count,
        available: available
      };
    }
    
    // Detach the assets
    this.detached_assets[assetCategory][assetType] += count;
    
    // Track destination for recall
    this.detached_assets.destinations.push({
      dest_facility_id: destFacilityId,
      asset_category: assetCategory,
      asset_type: assetType,
      count: count,
      detached_date: new Date().toISOString()
    });
    
    return {
      success: true,
      detached: count,
      remaining: available - count,
      effective: this.getEffectiveAssets()
    };
  }
  
  /**
   * Recall all detached assets
   * 
   * @returns {Object} Recall result
   */
  recallAllDetachedAssets() {
    const recalled = { ...this.detached_assets };
    
    // Reset detached assets
    this.detached_assets = {
      ground_ambulances: { wheeled_light: 0, wheeled_armored: 0, tracked: 0 },
      helicopters: { allocated: 0, on_call: 0 },
      personnel: { medics: 0, drivers: 0, litter_bearers: 0, flight_medics: 0 },
      destinations: []
    };
    
    return {
      success: true,
      recalled: recalled,
      effective: this.getEffectiveAssets()
    };
  }
  
  // ============================================================================
  // PHASE 2: EVACUATION CHAIN CONFIGURATION (EA-003)
  // ============================================================================
  
  /**
   * Set evacuation method
   * 
   * @param {string} direction - 'incoming' or 'outgoing'
   * @param {string} method - 'PULL', 'PUSH', or 'AUTO'
   */
  setEvacuationMethod(direction, method) {
    if (!['PULL', 'PUSH', 'AUTO'].includes(method)) {
      throw new Error(`Invalid evacuation method: ${method}`);
    }
    
    if (direction === 'incoming') {
      this.evacuation_config.incoming_method = method;
    } else if (direction === 'outgoing') {
      this.evacuation_config.outgoing_method = method;
    }
  }
  
  /**
   * Add a facility to pick up from (PULL relationship)
   * 
   * @param {string} facilityId - ID of facility to pick up from
   */
  addPickupSource(facilityId) {
    if (!this.evacuation_config.picks_up_from.includes(facilityId)) {
      this.evacuation_config.picks_up_from.push(facilityId);
    }
  }
  
  /**
   * Remove a pickup source
   * 
   * @param {string} facilityId - Facility ID to remove
   */
  removePickupSource(facilityId) {
    this.evacuation_config.picks_up_from = 
      this.evacuation_config.picks_up_from.filter(id => id !== facilityId);
  }
  
  /**
   * Set delivery destination
   * 
   * @param {string} facilityId - Destination facility ID
   */
  setDeliveryDestination(facilityId) {
    this.evacuation_config.delivers_to = facilityId;
  }
  
  /**
   * Get evacuation chain summary
   * 
   * @returns {Object} Evacuation chain configuration summary
   */
  getEvacuationChainSummary() {
    return {
      facility_id: this.facility_id,
      facility_name: this.facility_name,
      role_level: this.role_level,
      incoming_method: this.evacuation_config.incoming_method,
      outgoing_method: this.evacuation_config.outgoing_method,
      picks_up_from: this.evacuation_config.picks_up_from,
      delivers_to: this.evacuation_config.delivers_to,
      helicopter_priority: this.evacuation_config.helicopter_priority,
      max_hold_time_hours: this.evacuation_config.max_hold_time_hours,
      effective_assets: this.getEffectiveAssets()
    };
  }
  
  // ============================================================================
  // PHASE 2: EVACUATION CAPACITY CALCULATION
  // ============================================================================
  
  /**
   * Calculate daily evacuation capacity based on effective assets
   * 
   * @param {number} distanceKm - Distance to next echelon in km
   * @param {Object} environment - Environmental factors
   * @returns {Object} Evacuation capacity
   */
  calculateEvacuationCapacity(distanceKm = 20, environment = {}) {
    const effective = this.getEffectiveAssets();
    
    // Ground ambulance calculation
    // Assumptions: 4 patients per ambulance, 6 round trips per day at 20km
    const tripsPerDay = Math.max(1, 6 * (20 / distanceKm));
    const patientsPerAmbulance = 4;
    
    const groundCapacity = effective.ground_ambulances.total * patientsPerAmbulance * tripsPerDay;
    
    // Helicopter calculation
    // Assumptions: 6 patients per helicopter, 8 round trips per day
    const heliTripsPerDay = 8;
    const patientsPerHeli = 6;
    
    const airCapacity = effective.helicopters.total * patientsPerHeli * heliTripsPerDay;
    
    // Apply environmental modifiers
    let groundModifier = 1.0;
    let airModifier = 1.0;
    
    if (environment.drone_threat === 'HIGH') {
      groundModifier *= 0.7;
      airModifier *= 0.5;
    } else if (environment.drone_threat === 'EXTREME') {
      groundModifier *= 0.5;
      airModifier *= 0.2;
    }
    
    if (environment.weather === 'POOR') {
      groundModifier *= 0.8;
      airModifier *= 0.3;
    }
    
    return {
      ground: {
        ambulances: effective.ground_ambulances.total,
        trips_per_day: Math.round(tripsPerDay),
        patients_per_trip: patientsPerAmbulance,
        raw_capacity: Math.round(groundCapacity),
        effective_capacity: Math.round(groundCapacity * groundModifier),
        modifier_applied: groundModifier
      },
      air: {
        helicopters: effective.helicopters.total,
        trips_per_day: heliTripsPerDay,
        patients_per_trip: patientsPerHeli,
        raw_capacity: Math.round(airCapacity),
        effective_capacity: Math.round(airCapacity * airModifier),
        modifier_applied: airModifier
      },
      total: {
        raw_capacity: Math.round(groundCapacity + airCapacity),
        effective_capacity: Math.round(groundCapacity * groundModifier + airCapacity * airModifier)
      },
      distance_km: distanceKm,
      environment: environment
    };
  }
  
  // ============================================================================
  // CAPACITY MANAGEMENT METHODS (from v1.0)
  // ============================================================================
  
  getAvailableBeds() {
    return this.beds - this.beds_occupied;
  }
  
  getBedUtilization() {
    if (this.beds === 0) return 0;
    return (this.beds_occupied / this.beds) * 100;
  }
  
  canAcceptPatients(count) {
    return this.getAvailableBeds() >= count;
  }
  
  admitPatients(count) {
    const available = this.getAvailableBeds();
    
    if (count <= available) {
      this.beds_occupied += count;
      this.stats.total_patients_treated += count;
      this.stats.total_evacuations_received += count;
      
      return {
        success: true,
        admitted: count,
        overflow: 0,
        utilization: this.getBedUtilization()
      };
    } else {
      this.beds_occupied += available;
      this.stats.total_patients_treated += available;
      this.stats.total_evacuations_received += available;
      
      return {
        success: false,
        admitted: available,
        overflow: count - available,
        utilization: 100,
        warning: 'CAPACITY_EXCEEDED'
      };
    }
  }
  
  dischargePatients(count, reason = 'EVACUATED') {
    this.beds_occupied -= count;
    if (this.beds_occupied < 0) this.beds_occupied = 0;
    
    if (reason === 'EVACUATED') {
      this.stats.total_evacuations_sent += count;
    } else if (reason === 'RTD') {
      this.stats.total_returned_to_duty += count;
    } else if (reason === 'DOW') {
      this.stats.total_died_of_wounds += count;
    }
  }
  
  getCapacityStatus() {
    const utilization = this.getBedUtilization();
    
    let alert_level = 'GREEN';
    if (utilization >= 95) alert_level = 'BLACK';
    else if (utilization >= 90) alert_level = 'RED';
    else if (utilization >= 75) alert_level = 'YELLOW';
    
    return {
      beds_total: this.beds,
      beds_occupied: this.beds_occupied,
      beds_available: this.getAvailableBeds(),
      utilization_percentage: utilization,
      alert_level: alert_level,
      can_accept_more: this.getAvailableBeds() > 0
    };
  }
  
  // ============================================================================
  // SUPPLY MANAGEMENT (from v1.0)
  // ============================================================================
  
  consumeSupply(supply_type, quantity) {
    if (!this.supplies.hasOwnProperty(supply_type)) {
      return { success: false, error: 'UNKNOWN_SUPPLY_TYPE', shortage: quantity };
    }
    
    if (this.supplies[supply_type] >= quantity) {
      this.supplies[supply_type] -= quantity;
      return { success: true, consumed: quantity, remaining: this.supplies[supply_type], shortage: 0 };
    } else {
      const shortage = quantity - this.supplies[supply_type];
      const consumed = this.supplies[supply_type];
      this.supplies[supply_type] = 0;
      return { success: false, consumed: consumed, remaining: 0, shortage: shortage, warning: 'SUPPLY_SHORTAGE' };
    }
  }
  
  resupply(supplies) {
    for (const [type, quantity] of Object.entries(supplies)) {
      if (this.supplies.hasOwnProperty(type)) {
        this.supplies[type] += quantity;
      }
    }
  }
  
  // ============================================================================
  // PERSONNEL MANAGEMENT (from v1.0)
  // ============================================================================
  
  setPersonnel(personnel) {
    this.personnel = { ...this.personnel, ...personnel };
  }
  
  getTotalPersonnel() {
    return Object.values(this.personnel).reduce((sum, count) => sum + count, 0);
  }
  
  isAdequatelyStaffed() {
    return this.personnel.medical_officers >= 1 && 
           (this.personnel.medics + this.personnel.nurses) >= 2;
  }
  
  // ============================================================================
  // OPERATIONAL STATUS (from v1.0 with Phase 2 enhancements)
  // ============================================================================
  
  setOperationalStatus(operational) {
    this.operational = operational;
  }
  
  isFullyOperational() {
    if (!this.operational) return false;
    if (!this.isAdequatelyStaffed()) return false;
    if (this.supplies.trauma_kits === 0) return false;
    if (this.role_level >= 2 && this.supplies.blood_units === 0) return false;
    
    // Phase 2: Check evacuation assets
    const effective = this.getEffectiveAssets();
    if (effective.ground_ambulances.total === 0 && this.role_level <= 2) return false;
    
    return true;
  }
  
  getOperationalStatus() {
    return {
      operational: this.operational,
      adequately_staffed: this.isAdequatelyStaffed(),
      has_critical_supplies: this.supplies.trauma_kits > 0 && 
                              (this.role_level < 2 || this.supplies.blood_units > 0),
      has_evacuation_assets: this.getEffectiveAssets().ground_ambulances.total > 0,
      fully_operational: this.isFullyOperational(),
      issues: this.getOperationalIssues()
    };
  }
  
  getOperationalIssues() {
    const issues = [];
    
    if (!this.operational) issues.push('Facility marked non-operational');
    if (!this.isAdequatelyStaffed()) issues.push('Insufficient staff');
    if (this.supplies.trauma_kits === 0) issues.push('No trauma kits');
    if (this.role_level >= 2 && this.supplies.blood_units === 0) issues.push('No blood products');
    if (this.getBedUtilization() >= 95) issues.push('Capacity exceeded');
    
    // Phase 2: Check evacuation assets
    const effective = this.getEffectiveAssets();
    if (effective.ground_ambulances.total === 0 && this.role_level <= 2) {
      issues.push('No ground evacuation assets');
    }
    
    return issues;
  }
  
  // ============================================================================
  // STATISTICS & REPORTING (enhanced for Phase 2)
  // ============================================================================
  
  getStatistics() {
    return { ...this.stats };
  }
  
  resetStatistics() {
    this.stats = {
      total_patients_treated: 0,
      total_surgeries_performed: 0,
      total_evacuations_sent: 0,
      total_evacuations_received: 0,
      total_returned_to_duty: 0,
      total_died_of_wounds: 0,
      ground_evac_missions: 0,
      air_evac_missions: 0,
      average_evac_time_minutes: 0
    };
    this.beds_occupied = 0;
    this.patients_current = [];
  }
  
  // ============================================================================
  // SERIALIZATION (Phase 2 Enhanced)
  // ============================================================================
  
  toJSON() {
    return {
      // Identification
      facility_id: this.facility_id,
      facility_name: this.facility_name,
      role_level: this.role_level,
      facility_type: this.facility_type,
      parent_unit_id: this.parent_unit_id,
      parent_unit_type: this.parent_unit_type,
      hierarchy: this.hierarchy,
      
      // Positioning
      position_feba_km: this.position_feba_km,
      
      // Capacity
      beds: this.beds,
      beds_occupied: this.beds_occupied,
      surgical_teams: this.surgical_teams,
      icu_beds: this.icu_beds,
      
      // Personnel & Supplies
      personnel: this.personnel,
      supplies: this.supplies,
      
      // Phase 2: Evacuation Assets
      organic_assets: this.organic_assets,
      attached_assets: this.attached_assets,
      detached_assets: this.detached_assets,
      effective_assets: this.getEffectiveAssets(),
      
      // Phase 2: Evacuation Configuration
      evacuation_config: this.evacuation_config,
      evacuation_capacity: this.calculateEvacuationCapacity(),
      
      // Status
      operational: this.operational,
      capacity_status: this.getCapacityStatus(),
      operational_status: this.getOperationalStatus(),
      
      // Statistics
      statistics: this.stats,
      
      // Metadata
      created_at: this.created_at.toISOString(),
      version: this.version
    };
  }
  
  getSummary() {
    const effective = this.getEffectiveAssets();
    
    return {
      id: this.facility_id,
      name: this.facility_name,
      type: this.facility_type,
      role: this.role_level,
      position_km: this.position_feba_km,
      beds_available: this.getAvailableBeds(),
      beds_total: this.beds,
      utilization: this.getBedUtilization(),
      operational: this.isFullyOperational(),
      personnel_count: this.getTotalPersonnel(),
      // Phase 2 additions
      ambulances: effective.ground_ambulances.total,
      helicopters: effective.helicopters.total,
      evac_method: this.evacuation_config.outgoing_method
    };
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

// Already exported at definition

export default BaseMedicalFacility;
